/**
 * /admin/metrics — org-admin summary of agent turns, cost, latency and feedback.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert, AppBar, Box, Button, Card, CardContent, Container, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Toolbar, Typography,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import useAuth from '../hooks/useAuth';
import { formatTokens, formatUsd } from '../services/usage.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8003/api/v1';
const ADMIN_ROLES = ['admin', 'owner', 'org_admin', 'administrator'];

type Window = '24h' | '7d';

interface MetricsSummary {
  window: Window;
  turns: number;
  outcomes: Record<string, number>;
  routes: Record<string, { turns: number; cost_usd: number }>;
  cost_usd: number;
  tokens: { input: number; output: number; cache: number };
  errors: number;
  latency_ms: { p50: number | null; p95: number | null };
  models: Record<string, number>;
  steps: Record<string, number>;
  feedback: { up: number; down: number };
  cache_hit_ratio?: Record<string, number | null>;
  events?: Record<string, number>;
  top_parts?: { part: string; avg_chars: number; count: number }[];
}

const fetchSummary = async (window: Window, includeEval: boolean): Promise<MetricsSummary> => {
  const response = await fetch(
    `${API_URL}/admin/metrics/summary?window=${window}&include_eval=${includeEval}`,
    { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || 'Failed to load metrics');
  }
  return response.json();
};

const fmtPct = (r: number | null): string => (r == null ? '-' : `${(r * 100).toFixed(1)}%`);

const fmtMs = (ms: number | null): string => (ms == null ? '-' : `${(ms / 1000).toFixed(1)} s`);

const Stat: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Card variant="outlined" sx={{ minWidth: 150, flex: '1 1 150px' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5">{value}</Typography>
    </CardContent>
  </Card>
);

const CountTable: React.FC<{ title: string; head: string[]; rows: (string | number)[][] }> = ({ title, head, rows }) => (
  <Card variant="outlined" sx={{ flex: '1 1 300px' }}>
    <CardContent>
      <Typography variant="subtitle1" gutterBottom>{title}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>{head.map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={head.length}>No data</TableCell></TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={String(r[0])}>
              {r.map((c, i) => <TableCell key={i}>{c}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const AdminMetricsPage: React.FC = () => {
  const { user } = useAuth();
  const [win, setWin] = useState<Window>('24h');
  const isAdmin = useMemo(() => {
    const org =
      user?.organizations?.find((o) => o.organization_id === user.current_organization_id) ||
      user?.organizations?.[0];
    return !!org && ADMIN_ROLES.includes((org.role || '').toLowerCase());
  }, [user]);

  const q = useQuery({
    queryKey: ['admin', 'metrics', win],
    queryFn: () => fetchSummary(win, false),
    enabled: isAdmin,
  });
  const d = q.data;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Button component={RouterLink} to="/" startIcon={<BackIcon />}>Back</Button>
          <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>Agent metrics</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={win}
            onChange={(_, v: Window | null) => v && setWin(v)}
          >
            <ToggleButton value="24h">24 h</ToggleButton>
            <ToggleButton value="7d">7 d</ToggleButton>
          </ToggleButtonGroup>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {user && !isAdmin && <Alert severity="warning">Organization admin access required.</Alert>}
        {q.isError && <Alert severity="error">{(q.error as Error).message}</Alert>}
        {q.isLoading && <Typography>Loading...</Typography>}
        {d && (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Stat label="Turns" value={d.turns} />
              <Stat label="Errors" value={d.errors} />
              <Stat label="Cost" value={formatUsd(d.cost_usd)} />
              <Stat label="p50 latency" value={fmtMs(d.latency_ms.p50)} />
              <Stat label="p95 latency" value={fmtMs(d.latency_ms.p95)} />
              <Stat label="Thumbs up" value={d.feedback.up} />
              <Stat label="Thumbs down" value={d.feedback.down} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <CountTable
                title="Turns by route"
                head={['Route', 'Turns', 'Cost']}
                rows={Object.entries(d.routes).map(([k, v]) => [k, v.turns, formatUsd(v.cost_usd)])}
              />
              <CountTable
                title="Outcomes"
                head={['Outcome', 'Turns']}
                rows={Object.entries(d.outcomes)}
              />
              <CountTable
                title="Model calls"
                head={['Model', 'Calls']}
                rows={Object.entries(d.models)}
              />
              <CountTable
                title="Harness steps"
                head={['Kind', 'Count']}
                rows={Object.entries(d.steps)}
              />
              <CountTable
                title="Tokens"
                head={['Type', 'Tokens']}
                rows={[
                  ['Input', formatTokens(d.tokens.input)],
                  ['Output', formatTokens(d.tokens.output)],
                  ['Cache', formatTokens(d.tokens.cache)],
                ]}
              />
              <CountTable
                title="Cache hit ratio by route"
                head={['Route', 'Hit ratio']}
                rows={Object.entries(d.cache_hit_ratio ?? {}).map(([k, v]) => [k, fmtPct(v)])}
              />
              <CountTable
                title="Parse errors, fallbacks, refusals"
                head={['Event', 'Count']}
                rows={Object.entries(d.events ?? {})}
              />
              <CountTable
                title="Top prompt parts (avg chars)"
                head={['Part', 'Avg chars', 'Calls']}
                rows={(d.top_parts ?? []).map((p) => [p.part, Math.round(p.avg_chars), p.count])}
              />
            </Box>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default AdminMetricsPage;
